<template>
  <div class="w-[280px]">
    <message-reactions-viewer
      v-if="activePanel === 'reactions'"
      :event-id="event.eventId"
      @back="activePanel = 'actions'"
    />
    <message-views-viewer
      v-else-if="activePanel === 'views'"
      :event-id="event.eventId"
      :p-served="pServed"
      @back="activePanel = 'actions'"
    />
    <template v-else>
      <div
        v-if="event.item.type === 'IncomingMessageEvent' && event.item.executor !== currentUser?.imLogin"
        ref="emojiMenu"
        class="mb-2"
      >
        <reaction-context-menu
          :p-served="pServed"
          :event-id="event.eventId"
          @close-reaction-menu="emit('close')"
        />
      </div>
      <chat-message-context-menu
        :event="event"
        :selected-file="selectedFile"
        :is-link="!!selectedLink"
        :is-selected="selectedEventsIds.has(event.eventId)"
        :is-selection-mode="selectedEventsIds.size > 0"
        :hide-quote="hideQuotes!"
        :events-length="eventsLength ?? 0"
        :hide-delete="hideDelete!"
        :show-delete-for-self="showDeleteForSelf!"
        :hide-mark-as-unread="isGuestUser"
        :is-text-selected="!!selectedMessageText && selectedMessageText !== event.item.messageText"
        :show-in-chat="showInChat"
        @copy-message="onCopyMessage"
        @copy-selected-message-text="onCopySelectedMessageText"
        @copy-link="onCopyLink()"
        @quote-message="onStartQuote(event.eventId)"
        @pin-message="onPinMessage(event)"
        @unpin-message="onUnpinMessage(event)"
        @start-edit="onStartEdit(event)"
        @remove-message="onRemoveMessage(event)"
        @remove-message-for-self="onRemoveMessageForSelf(event)"
        @forward-message="onForwardMessage(event)"
        @select-multiple="emit('select', event.eventId)"
        @resend="onResendMessage(event as HistoryMessageItem<MessageOutgoingEvent>)"
        @mark-as-unread="onMarkAsUnread(event.eventId)"
        @delete-mark-as-unread="onDeleteMarkAsUnread(event.eventId)"
        @show-in-chat="emit('showInChat')"
        @view-reactions="activePanel = 'reactions'"
        @view-views="onViewViews"
        @hide-menu="emit('close')"
      />
    </template>
  </div>
</template>
<script setup lang="ts">
import { useDialog, useRenderNotifier } from '@wui/common-library'
import { HistoryMessageItem, MessageIncomingEvent, MessageOutgoingEvent, PServed } from '@wui/im'
import { computed, nextTick, ref, toRef, watch } from 'vue'

import { ChatForwardMessage } from '@/widgets/chat-forward-message-widget'

import { useMarkUnread } from '@/features/mark-message-as-unread'
import { useMessageEdit } from '@/features/message-edit'
import { MessageReactionsViewer } from '@/features/message-reactions-viewer'
import { MessageViewsViewer } from '@/features/message-views-viewer'
import { ReactionContextMenu } from '@/features/reaction-context-menu'

import { usePinEventsStore } from '@/entities/messages'
import { useRoom } from '@/entities/use-room'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { isGuest } from '@/shared/composables/local-storage'
import { useLocalization } from '@/shared/composables/localization'
import { placeCaretAtEnd } from '@/shared/utils/contenteditable-selection'
import { copyRichTextToClipboard } from '@/shared/utils/copy-rich-text-to-clipboard'
import { formatMessageText } from '@/shared/utils/text-entities'

import { useStartQuote } from '../lib/use-start-quote'

import ChatMessageContextMenu from './chat-message-context-menu.vue'

const props = defineProps<{
  pServed: PServed
  event: HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent>
  selectedFile: string | null
  selectedLink: HTMLElement | null
  selectedMessageText: string
  selectedMessageHtml: string
  selectedEventsIds: Set<number>
  hideQuotes?: boolean
  eventsLength?: number
  hideDelete?: boolean
  showDeleteForSelf?: boolean
  showInChat?: boolean
}>()

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'select', value: HistoryMessageItem['eventId']): void
  (event: 'showInChat'): void
}>()

type ContextMenuPanel = 'actions' | 'reactions' | 'views'

const activePanel = ref<ContextMenuPanel>('actions')
const selectedMessageText = toRef(props, 'selectedMessageText')

const { currentUser } = useCurrentUser()
const { currentGuest } = useGuestAuth()
const isGuestUser = computed(() => isGuest() || Boolean(currentGuest.value))
const { notifyError } = useRenderNotifier()
const { showDialog } = useDialog()
const { getPinEventsForRoom } = usePinEventsStore()
const { startEdit } = useMessageEdit()
const { onStartQuote } = useStartQuote(props.pServed, selectedMessageText)
const { t } = useLocalization()
const { onMarkAsUnread, onDeleteMarkAsUnread } = useMarkUnread(props.pServed)

watch(() => props.event.eventId, () => {
  activePanel.value = 'actions'
})

const onViewViews = () => {
  activePanel.value = 'views'
}

const onCopyMessage = () => {
  const { messageText = '', entities } = props.event.item
  void copyRichTextToClipboard({
    plainText: messageText,
    html: formatMessageText(messageText, entities),
  })
}

const onCopySelectedMessageText = () => {
  const html = props.selectedMessageHtml
  void copyRichTextToClipboard(
    html
      ? { plainText: props.selectedMessageText, html }
      : { plainText: props.selectedMessageText },
  )
}

const onCopyLink = () => {
  const href = props.selectedLink?.getAttribute('href')
  if (!href) {
    return
  }

  navigator.clipboard.writeText(href)
}

const roomApi = useRoom()

const onPinMessage = (event: HistoryMessageItem) => {
  roomApi.pin(event.eventId, props.pServed)
}

const onUnpinMessage = (event: HistoryMessageItem) => {
  roomApi.deletePin(event.eventId, props.pServed)
}

const onStartEdit = async (event: HistoryMessageItem) => {
  startEdit(event as HistoryMessageItem<MessageOutgoingEvent>)

  const contenteditable: HTMLElement | null = document.getElementById('contenteditable')
  if (contenteditable) {
    await nextTick()
    placeCaretAtEnd(contenteditable)
  }
}

const onForwardMessage = (event: HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent>) => {
  showDialog(
    ChatForwardMessage,
    {
      chatEvents: [event],
      hasOverlay: true,
      selectedText: selectedMessageText.value,
    },
  )
}

const onRemoveMessage = (event: HistoryMessageItem) => {
  try {
    roomApi.delete(event.eventId, props.pServed).then((response: boolean) => {
      if (response) {
        const pinEvents = getPinEventsForRoom(props.pServed)
        const pinEvent = pinEvents?.find((i) => i.eventId === event.eventId)
        if (pinEvent && event.eventId === pinEvent.eventId) {
          roomApi.deletePin(pinEvent.eventId, props.pServed)
        }
      }
    })
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
    notifyError(errorMessage)
  }
}

const onRemoveMessageForSelf = (event: HistoryMessageItem) => {
  try {
    roomApi.deleteForSelf(event.eventId, props.pServed).then((response: boolean) => {
      if (response) {
        const pinEvents = getPinEventsForRoom(props.pServed)
        const pinEvent = pinEvents?.find((i) => i.eventId === event.eventId)
        if (pinEvent && event.eventId === pinEvent.eventId) {
          roomApi.deletePin(pinEvent.eventId, props.pServed)
        }
      }
    })
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
    notifyError(errorMessage)
  }
}

const onResendMessage = async (event: HistoryMessageItem<MessageOutgoingEvent>) => {
  try {
    await roomApi.resend(event, props.pServed)
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
    notifyError(errorMessage)
  }
}
</script>
